/**
 * The wallpaper behind GOOD BUILD OS.
 *
 * A console does not sit on a flat panel: the room takes its colour from whatever is on the
 * shelf. Every screen can hand this layer a picture — the featured game, the game being
 * looked at — and the shell blurs it back far enough that nothing has
 * to be read against it. Screens that have no artwork fall back to the wallpaper that ships
 * inside the app, so the desktop build and the handheld look right before they have a network.
 *
 * Two rules keep it from becoming decoration that costs something:
 *
 * - It is blurred, dimmed and scrimmed here, in one place, rather than by each screen. Text is
 *   read against the scrim, never against the picture (field rule 03).
 * - It is a setting. On a handheld a full-screen blur is work the GPU is doing for the room
 *   rather than for the game, and `prefers-reduced-transparency` turns it off outright.
 */

import * as React from 'react';
import { useSettings } from '../../lib/settings.js';

/** The wallpaper that ships with the app, used by every screen with no artwork of its own. */
export const DEFAULT_BACKDROP = '/backdrop.svg';

/** Matches `--gb-duration-wallpaper`. The old picture is dropped once the new one has landed. */
const FADE_MS = 700;

interface Claim {
  id: number;
  src: string | null;
  /** `#rrggbb`, or null for a screen that is a list rather than a thing. See `accent.ts`. */
  accent: string | null;
}

interface BackdropApi {
  src: string | null;
  accent: string | null;
  claim: (id: number, src: string | null, accent: string | null) => void;
  release: (id: number) => void;
}

const BackdropContext = React.createContext<BackdropApi>({
  src: null,
  accent: null,
  claim: () => {},
  release: () => {},
});

let nextClaimId = 1;

/**
 * Holds whatever the deepest mounted screen asked for.
 *
 * Claims rather than one value, because a route change mounts the next screen before it
 * unmounts the last one: with a single slot the screen leaving would clear the picture the
 * screen arriving just set.
 */
export function BackdropProvider({ children }: { children: React.ReactNode }) {
  const [claims, setClaims] = React.useState<Claim[]>([]);

  const claim = React.useCallback((id: number, src: string | null, accent: string | null) => {
    setClaims((prev) => {
      const existing = prev.find((c) => c.id === id);
      if (existing) {
        if (existing.src === src && existing.accent === accent) return prev;
        return prev.map((c) => (c.id === id ? { id, src, accent } : c));
      }
      return [...prev, { id, src, accent }];
    });
  }, []);

  const release = React.useCallback((id: number) => {
    setClaims((prev) => (prev.some((c) => c.id === id) ? prev.filter((c) => c.id !== id) : prev));
  }, []);

  const top = claims.length > 0 ? claims[claims.length - 1] : undefined;
  const src = top?.src ?? null;
  const accent = top?.accent ?? null;

  /*
   * On the document rather than on a wrapper, because the two layers that draw it are on
   * opposite sides of the tree: the wallpaper is behind the whole shell and the cold open is
   * inside a route. One custom property both can read is cheaper than threading a colour
   * through every screen between them.
   *
   * Cleared rather than left behind when nothing claims one, or the last game looked at would
   * go on lighting the settings screen.
   */
  React.useEffect(() => {
    const root = document.documentElement;
    if (accent) root.style.setProperty('--gb-accent', accent);
    else root.style.removeProperty('--gb-accent');
  }, [accent]);

  const value = React.useMemo<BackdropApi>(
    () => ({ src, accent, claim, release }),
    [src, accent, claim, release],
  );

  return <BackdropContext.Provider value={value}>{children}</BackdropContext.Provider>;
}

/**
 * Puts this screen's artwork behind the whole app while the screen is open, and lights the room
 * in its colour.
 *
 * `null` or nothing is the wallpaper the app ships with and no accent, which is the right answer
 * for every screen that is a list rather than a thing.
 */
export function useBackdrop(
  src: string | null | undefined,
  accent?: string | null | undefined,
): void {
  const { claim, release } = React.useContext(BackdropContext);
  const id = React.useMemo(() => nextClaimId++, []);

  React.useEffect(() => {
    claim(id, src ?? null, accent ?? null);
  }, [claim, id, src, accent]);

  React.useEffect(() => () => release(id), [release, id]);
}

interface Layer {
  key: number;
  src: string;
}

let nextLayerKey = 1;

export function Wallpaper() {
  const { src } = React.useContext(BackdropContext);
  const { ambientArt, reduceMotion } = useSettings();
  // A picture that will not load is not worth keeping a screen dark for: the shipped wallpaper
  // takes over, and the next screen gets its own chance.
  const [broken, setBroken] = React.useState<string | null>(null);

  const wanted = ambientArt && src && src !== broken ? src : DEFAULT_BACKDROP;
  const [layers, setLayers] = React.useState<Layer[]>(() => [{ key: 0, src: wanted }]);

  React.useEffect(() => {
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top && top.src === wanted) return prev;
      // Two at a time: the one being crossed to, and the one being crossed from.
      return [...prev, { key: nextLayerKey++, src: wanted }].slice(-2);
    });
  }, [wanted]);

  // Once the top layer has arrived, the one underneath is doing nothing but holding a decoded
  // image and a blur. Dropping it keeps this to one composited picture while nothing changes.
  //
  // With motion turned off there is no dissolve to wait for: the stylesheet drops the animation,
  // so the old picture has to be taken away rather than faded, or it sits at full under the new
  // one and the room brightens for most of a second.
  React.useEffect(() => {
    if (layers.length < 2) return;
    const timer = setTimeout(
      () => setLayers((prev) => prev.slice(-1)),
      reduceMotion ? 0 : FADE_MS + 80,
    );
    return () => clearTimeout(timer);
  }, [layers, reduceMotion]);

  return (
    <div className="gb-wallpaper" aria-hidden="true">
      {layers.map((layer, index) => (
        <img
          key={layer.key}
          className="gb-wallpaper__layer"
          // The one being crossed from fades out as the new one fades in. Without that they
          // are both at full for the moment before the old one is dropped, and the room
          // brightens and settles for no reason anybody asked for.
          data-leaving={index < layers.length - 1 || undefined}
          src={layer.src}
          alt=""
          decoding="async"
          // It is the last thing on the screen that matters, so it is the last thing fetched.
          fetchPriority="low"
          draggable={false}
          onError={() => {
            if (layer.src !== DEFAULT_BACKDROP) setBroken(layer.src);
          }}
        />
      ))}
      <span className="gb-wallpaper__scrim" />
    </div>
  );
}
